import React, { FunctionComponent, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Paper,
  Switch,
  makeStyles,
  Avatar,
  Grid,
  Button,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { 
  RealocacaoPedidosMpItens, 
  RealocacaoItem,
  determinarResponsavel, 
  determinarStatusFaturamento, 
  StatusFaturamento,
  formatarData,
  formatarNumero 
} from '../types/RealocacaoPedidosMpItens.types';
import ItensRealocacaoDrawer from './ItensRealocacaoDrawer';

const useStyles = makeStyles({
  accordion: {
    borderRadius: '16px !important',
    boxShadow: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    '&:before': {
      display: 'none',
    },
    overflow: 'hidden',
  },
  accordionSummary: {
    padding: '0 24px',
    minHeight: '40px',
    marginTop: '8px',
    '& .MuiAccordionSummary-content': {
      margin: '0',
    },
  },
  accordionDetails: {
    padding: '0 24px 24px 24px',
    flexDirection: 'column',
  },
  titleContainer: {
    paddingBottom: '12px',
    width: '100%',
  },
  title: {
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '4px',
    fontSize: '15px',
  },
  titleUnderline: {
    width: '100%',
    height: '2px',
    background: '#97877A',
    borderRadius: '4px',
    opacity: 0.5,
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#333',
    marginBottom: '12px',
    marginTop: '22px',
    fontSize: '14px',
    letterSpacing: '0.2px',
    display: 'flex',
    alignItems: 'center',
    '& > *:not(:last-child)': {
      marginRight: '6px',
    },
  },
  sectionIcon: {
    fontSize: '16px',
    color: '#666',
  },

  headerPaper: {
    padding: '16px 20px',
    borderRadius: '10px',
    border: '1px solid #ebebeb',
    backgroundColor: '#fafafa',
    boxShadow: 'none',
  },
  label: {
    color: '#777',
    fontWeight: 600,
    fontSize: '10px',
    textTransform: 'uppercase' as const,
    marginBottom: '3px',
    letterSpacing: '0.4px',
  },
  value: {
    fontWeight: 600,
    color: '#222',
    fontSize: '13px',
    lineHeight: 1.3,
  },
  valueSmall: {
    fontWeight: 500,
    color: '#555',
    fontSize: '12px',
    lineHeight: 1.3,
  },
  materialCard: {
    padding: '14px 18px',
    flex: 1,
    borderRadius: '10px',
    border: '1px solid #ebebeb',
    backgroundColor: '#fafafa',
    boxShadow: 'none',
  },
  labelGold: {
    color: '#b8960c',
    fontWeight: 700,
    fontSize: '10px',
    textTransform: 'uppercase' as const,
    marginBottom: '3px',
    letterSpacing: '0.4px',
  },

  triangulacaoHeader: {
    padding: '8px 16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px 8px 0 0',
    border: '1px solid #ebebeb',
    borderBottom: 'none',
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 1.1fr 1.1fr 0.8fr 80px',
    columnGap: '8px',
  },
  itemRow: {
    padding: '12px 16px',
    backgroundColor: '#fff',
    border: '1px solid #ebebeb',
    borderTop: 'none',
    '&:last-child': {
      borderRadius: '0 0 8px 8px',
    },
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 1.1fr 1.1fr 0.8fr 80px',
    columnGap: '8px',
    alignItems: 'center',
    '& > *': {
      minWidth: 0,
    },
    '&:hover': {
      backgroundColor: '#fcfcfc',
    },
  },
  itemValue: {
    fontSize: '10.5px',
    color: '#2a2a2a',
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  itemValueSub: {
    fontSize: '10.5px',
    color: '#666',
    marginTop: '2px',
    lineHeight: 1.2,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },


  statusFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '24px',
    padding: '12px 20px',
    backgroundColor: '#fcfcfc',
    borderRadius: '10px',
    border: '1px solid #ebebeb',
  },
  avatar: {
    width: '32px',
    height: '32px',
    fontSize: '14px',
    backgroundColor: '#444',
  },
  actionButton: {
    fontSize: '11px',
    padding: '4px 12px',
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: '#fff',
    border: '1px solid #ebebeb',
    color: '#555',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      borderColor: '#ddd',
    },
  },
});

interface DadosRealocacaoAccordionProps {
  data: RealocacaoPedidosMpItens;
  defaultExpanded?: boolean;
}

const DadosRealocacaoAccordion: FunctionComponent<DadosRealocacaoAccordionProps> = ({
  data,
  defaultExpanded = true,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RealocacaoItem | null>(null);

  const statusFaturamento = determinarStatusFaturamento(data.dataFaturamento);
  const responsavel = determinarResponsavel(data.dataFaturamento);
  const responsavelTexto = responsavel === 'COMPRADORA' ? 'Compradora' : 'Responsável PCP';
  const isFaturado = statusFaturamento === StatusFaturamento.FATURADO;

  const handleOpenDrawer = (item: RealocacaoItem) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedItem(null);
  };

  return (
    <Accordion
      className={classes.accordion}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      elevation={0}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon style={{ fontSize: 20 }} />}
        className={classes.accordionSummary}
      >
        <Box className={classes.titleContainer}>
          <Typography className={classes.title}>Dados da Realocação</Typography>
          <Box className={classes.titleUnderline} />
        </Box>
      </AccordionSummary>

      <AccordionDetails className={classes.accordionDetails}>
        
        <Typography className={classes.sectionTitle}>
          Pedido para realocar
        </Typography>
        <Paper className={classes.headerPaper} elevation={0}>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <Typography className={classes.label}>Pedido</Typography>
              <Typography className={classes.value}>{data.pedidoMp || '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Material</Typography>
              <Typography className={classes.value} title={data.material}>{data.descMaterial || '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Cor Material</Typography>
              <Typography className={classes.value} title={data.corMaterial}>{data.descCorMaterial || '-'}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.label}>Qtde Realocar</Typography>
              <Typography className={classes.value}>{formatarNumero(data.quantidadeRealocar)}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.label}>Facção Atual Pedido</Typography>
              <Typography className={classes.value}>{data.faccao || '-'}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Typography className={classes.sectionTitle}>
          Outras informações do pedido
        </Typography>
        <Paper className={classes.headerPaper} elevation={0}>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <Typography className={classes.label}>Fornecedor MP</Typography>
              <Typography className={classes.value}>{data.fornecedorMp || '-'}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.label}>Compradora</Typography>
              <Typography className={classes.value}>{data.compradora || '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Emissão</Typography>
              <Typography className={classes.value}>{formatarData(data.dataEmissaoPedido)}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Faturamento</Typography>
              <Typography className={classes.value}>{formatarData(data.dataFaturamento)}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.label}>Status</Typography>
              <Typography className={classes.value} style={{ fontSize: '11px', color: isFaturado ? '#2e7d32' : '#666' }}>
                {data.pedidoFaturado}
              </Typography>
            </Grid>
          </Grid>
        </Paper>



        <Typography className={classes.sectionTitle}>
          Triangulações
        </Typography>
        
        <Box>
          <Box className={classes.triangulacaoHeader}>
            <Typography className={classes.label}>Produto</Typography>
            <Typography className={classes.label}>Prog.</Typography>
            <Typography className={classes.label}>Ped. MP</Typography>
            <Typography className={classes.label}>Material</Typography>
            <Typography className={classes.label}>Cor</Typography>
            <Typography className={classes.label} style={{ textAlign: 'center' }}>Ação</Typography>
          </Box>
          
          {data.itens.map((item, index) => (
            <Box key={item.id_item || index} className={classes.itemRow}>

              <Typography className={classes.itemValue} title={item.produto || '-'}>
                {item.produto || '-'}
              </Typography>
              
              <Typography className={classes.itemValue} title={item.programacao || '-'}>{item.programacao || '-'}</Typography>

              <Typography className={classes.itemValue} title={item.pedidoMp || '-'}>{item.pedidoMp || '-'}</Typography>

              <Typography className={classes.itemValue} title={item.material || '-'}>{item.material || '-'}</Typography>

              <Typography className={classes.itemValue} title={item.corMaterial || '-'}>{item.corMaterial || '-'}</Typography>

              <Box style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  className={classes.actionButton}
                  size="small"
                  startIcon={<VisibilityIcon style={{ fontSize: 14 }} />}
                  onClick={() => handleOpenDrawer(item)}
                >
                  Ver itens
                </Button>
              </Box>
            </Box>
          ))}
        </Box>


        <Box className={classes.statusFooter}>
          <Box display="flex" alignItems="center">
            <Avatar className={classes.avatar}>{responsavelTexto.charAt(0)}</Avatar>
            <Box ml={1.5}>
              <Typography className={classes.label} style={{ marginBottom: 0 }}>Responsável</Typography>
              <Typography className={classes.value} style={{ fontSize: '13px' }}>{responsavelTexto}</Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <Switch size="small" checked={isFaturado} disabled color="default" style={{ marginRight: '4px' }} />
            <Typography className={classes.value} style={{ fontSize: '11px', color: '#666' }}>
              {isFaturado ? 'FATURADO' : 'PENDENTE'}
            </Typography>
          </Box>
        </Box>

      </AccordionDetails>

      <ItensRealocacaoDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        item={selectedItem}
      />
    </Accordion>
  );
};

export default DadosRealocacaoAccordion;
